import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/services/firebase'
import type { User, UserCreatePayload, UserUpdatePayload } from '@/types'
import { getInstitute } from '@/services/institute.service'

interface ServiceError {
  code: string
  message: string
  originalError: unknown
}

function createServiceError(originalError: unknown): ServiceError {
  const err = originalError as { code?: string; message?: string }
  return {
    code: err.code ?? 'unknown',
    message: err.message ?? 'An unexpected error occurred',
    originalError,
  }
}

/**
 * Fetch a single User document by UID from the `users` collection.
 *
 * The document ID is the Firebase Auth UID. Returns the User with `uid`
 * set from the document ID, or null if not found. Firestore rules restrict
 * full reads to superuser or the user themselves; other users may only read
 * limited fields (`{uid, email, displayName}`) if they share an institute.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function getUser(uid: string): Promise<User | null> {
  try {
    const snap = await getDoc(doc(db, 'users', uid))
    if (!snap.exists()) return null
    return { uid: snap.id, ...snap.data() } as User
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch all documents from the `users` collection without filtering.
 *
 * Returns every User the authenticated caller has read access to. Only
 * superusers have full list access; other roles are restricted by
 * Firestore Security Rules.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function listUsers(): Promise<User[]> {
  try {
    const snap = await getDocs(collection(db, 'users'))
    return snap.docs.map((d) => ({ uid: d.id, ...d.data() }) as User)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Create a User document in the `users` collection using the UID as
 * the document ID (via `setDoc`, not `addDoc`).
 *
 * Stamps both `createdAt` and `updatedAt` with `serverTimestamp()`.
 * Also writes the `uid` into the document body for query convenience.
 * Called during registration after Firebase Auth user creation.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function createUser(uid: string, payload: UserCreatePayload): Promise<void> {
  try {
    await setDoc(doc(db, 'users', uid), {
      ...payload,
      uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Update an existing User document by merging the given fields.
 *
 * Writes to `users/{uid}` and re-stamps `updatedAt` with
 * `serverTimestamp()`. Used for role changes, profile updates, and
 * institute association changes.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function updateUser(uid: string, payload: Partial<UserUpdatePayload>): Promise<void> {
  try {
    await updateDoc(doc(db, 'users', uid), {
      ...payload,
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Delete a User document from the `users` collection by UID.
 *
 * Permanently removes the Firestore document. Does not delete the
 * Firebase Auth account — that must be handled separately. Firestore
 * rules restrict deletion to superuser.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function deleteUser(uid: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'users', uid))
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch all users who are members of the given institute.
 *
 * Reads the institute member arrays, deduplicates their UIDs, then fetches the
 * corresponding `users/{uid}` documents. Institute membership is sourced from
 * the institute document rather than a duplicated field on user records.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function getUsersByInstituteId(instituteId: string): Promise<User[]> {
  try {
    const institute = await getInstitute(instituteId)
    if (!institute) return []

    const memberUids = [...new Set([
      ...(institute.managerIds ?? []),
      ...(institute.tutorIds ?? []),
      ...(institute.studentIds ?? []),
    ])]

    if (memberUids.length === 0) return []

    const snaps = await Promise.all(memberUids.map((uid) => getDoc(doc(db, 'users', uid))))
    return snaps
      .filter((snap) => snap.exists())
      .map((snap) => ({ uid: snap.id, ...snap.data() }) as User)
  } catch (error) {
    throw createServiceError(error)
  }
}
